import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  GridColumn,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { ComboBox, DropDownList } from "@progress/kendo-react-dropdowns";
import { Switch } from "@progress/kendo-react-inputs";
import { Drawer, DrawerContent, DrawerNavigation } from "@progress/kendo-react-layout";
import { FaFileExcel, FaPlus, FaTimesCircle } from "react-icons/fa";
import { LuChevronsUp, LuChevronsDown } from "react-icons/lu";
import { IconType } from "react-icons";
import dayjs from "dayjs";
import { getNotesWithPagination, getNoteTypesForFilterDropdown, getNoteDetailsForFilterDropdown, addNoteDTO, notesGridDTO, addStandaloneNote, toggleCompletedNote, getNotePriorities, updateNotePriority, notePriority, NOTE_PRIORITY_NORMAL, getViewableEmployees, viewableEmployee } from "../../data/notes";
import { useToast } from "../../contexts/ToastContext";
import { useSessionState } from "../../helpers/useSessionState";
import AddNoteForm from "../../components/FormComps/Notes/AddNoteForm";
import "../../components/grids/Grids.css";
import * as XLSX from "xlsx";

// Add inline styles to ensure filter inputs are visible
const filterInputStyles = `
  .k-grid .k-filtercell input,
  .k-grid .k-filtercell .k-textbox,
  .k-grid .k-filtercell .k-input-inner,
  .k-grid input.k-input-inner,
  .k-grid .k-filtercell input.k-textbox,
  .k-grid .k-filtercell-wrapper input,
  .k-grid th .k-filtercell input,
  .k-grid-header .k-filtercell input {
    color: #000 !important;
    background-color: #fff !important;
    opacity: 1 !important;
    -webkit-text-fill-color: #000 !important;
  }
`;

// High and Low get a tinted badge; Normal notes stay unmarked, so a column of
// mostly-normal notes reads quietly and the exceptions are what catch the eye.
const PRIORITY_BADGES: Record<
  string,
  { color: string; background: string; Icon: IconType; label: string }
> = {
  High: {
    color: "#dc2626",
    background: "rgba(220, 38, 38, 0.12)",
    Icon: LuChevronsUp,
    label: "High priority",
  },
  Low: {
    color: "#2563eb",
    background: "rgba(37, 99, 235, 0.12)",
    Icon: LuChevronsDown,
    label: "Low priority",
  },
};

// Sentinel for the "All" option in the priority filter — no real priority uses 0
const ALL_PRIORITIES = 0;

const PriorityIcon = ({ priority }: { priority?: string }) => {
  const badge = priority ? PRIORITY_BADGES[priority] : undefined;

  if (!badge) return null;

  const { color, background, Icon, label } = badge;

  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "24px",
        height: "24px",
        borderRadius: "7px",
        backgroundColor: background,
        color,
      }}
    >
      <Icon size={15} />
    </span>
  );
};

// Custom text filter cell for reference column
const ReferenceFilterCell = (props: any) => {
  const { onChange, value } = props;
  const [inputValue, setInputValue] = useState(value || '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange({
        value: newValue || undefined,
        operator: newValue ? 'contains' : '',
        syntheticEvent: e,
        field: 'reference'
      });
    }
  };

  return (
    <input
      type="text"
      value={inputValue}
      onChange={handleChange}
      placeholder="Filter..."
      style={{
        width: '100%',
        padding: '4px 8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '14px',
        color: '#000',
        backgroundColor: '#fff',
        boxSizing: 'border-box'
      }}
    />
  );
};

// Custom text filter cell for details column
const DetailsTextFilterCell = (props: any) => {
  const { onChange, value } = props;
  const [inputValue, setInputValue] = useState(value || '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange({
        value: newValue || undefined,
        operator: newValue ? 'contains' : '',
        syntheticEvent: e,
        field: 'note'
      });
    }
  };

  return (
    <input
      type="text"
      value={inputValue}
      onChange={handleChange}
      placeholder="Filter..."
      style={{
        width: '100%',
        padding: '4px 8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '14px',
        color: '#000',
        backgroundColor: '#fff',
        boxSizing: 'border-box'
      }}
    />
  );
};

// Filter cells for Type and Details
const TypeFilterCell = (props: any) => {
  const { field, filter, onChange } = props;
  const [selectedValue, setSelectedValue] = useState(filter?.value || null);
  const [dropdownData, setDropdownData] = useState<any[]>([]);

  useEffect(() => {
    const loadTypes = async () => {
      const types = await getNoteTypesForFilterDropdown("");
      setDropdownData(types);
    };
    loadTypes();
  }, []);

  // Sync selectedValue with filter prop changes
  useEffect(() => {
    if (!filter) {
      setSelectedValue(null);
    }
  }, [filter]);

  const handleFilterChange = useCallback((e: any) => {
    const targetValue = e.target.value;
    setSelectedValue(targetValue);
    
    if (onChange) {
      if (!targetValue || targetValue === null || targetValue === '') {
        // Clear the filter completely - pass undefined value
        onChange({
          value: undefined,
          operator: "",
          syntheticEvent: e.syntheticEvent
        });
      } else {
        const newValue = targetValue?.value ?? targetValue;
        onChange({
          value: newValue,
          operator: "eq",
          syntheticEvent: e.syntheticEvent
        });
      }
    }
  }, [field, onChange]);

  return (
    <ComboBox
      data={dropdownData}
      textField="text"
      value={selectedValue}
      onChange={handleFilterChange}
      placeholder="Filter by type..."
      style={{ width: "100%" }}
    />
  );
};

// Who has completed a note, and when. Completion is per assignee, so this is a
// list rather than a single value.
const CompletionBreakdown: React.FC<{ note: notesGridDTO }> = ({ note }) => {
  const assignments = note.assignments ?? [];

  if (assignments.length === 0) {
    return (
      <div style={{ color: '#666' }}>
        No one assigned &mdash; {note.isCompleted ? 'marked completed' : 'not completed'}
      </div>
    );
  }

  return (
    <>
      {assignments.map((assignment) => (
        <div
          key={assignment.employeeId}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1rem',
            padding: '0.15rem 0',
          }}
        >
          <span>{assignment.employeeName}</span>
          <span
            style={{
              whiteSpace: 'nowrap',
              color: assignment.isCompleted ? '#2d5016' : '#8a6d00',
            }}
          >
            {assignment.isCompleted
              ? assignment.completedDate
                ? `Completed ${dayjs(assignment.completedDate).format('DD/MM/YYYY HH:mm')}`
                : 'Completed'
              : 'Outstanding'}
          </span>
        </div>
      ))}
    </>
  );
};

const NotesScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [paginatedRows, setPaginatedRows] = useState<notesGridDTO[]>([]);
  const [total, setTotal] = useState(0);
  // Page size, sort and filters persist for the session; page position resets on re-entry
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useSessionState("notes.take", 20);
  const [sort, setSort] = useSessionState<any[]>("notes.sort", []);
  const [filter, setFilter] = useSessionState<any>("notes.filter", null);
  const [showCompleted, setShowCompleted] = useSessionState("notes.showCompleted", false);
  // 0 means every priority. Filtering happens server-side, since the grid is
  // paged there and a client-side filter would only apply to the current page.
  const [priorityFilter, setPriorityFilter] = useSessionState<number>("notes.priorityFilter", ALL_PRIORITIES);
  const [expanded, setExpanded] = useState(false);
  const [addFormVisible, setAddFormVisible] = useState(false);
  const [viewFormVisible, setViewFormVisible] = useState(false);
  const [selectedNote, setSelectedNote] = useState<notesGridDTO | null>(null);
  const [currentUserEmployee, setCurrentUserEmployee] = useState<{ value: number; text: string } | null>(null);
  const [priorityOptions, setPriorityOptions] = useState<{ value: number; text: string }[]>([]);
  // Themselves, plus anyone whose notes they have been granted sight of. One
  // entry means no grants, and the picker stays hidden.
  const [viewableEmployees, setViewableEmployees] = useState<viewableEmployee[]>([]);
  // Whose notes are on screen. Starts as null, which the API reads as "mine",
  // so the screen opens on their own notes exactly as it did before.
  const [viewEmployeeId, setViewEmployeeId] = useState<number | null>(null);
  const [hoveredNote, setHoveredNote] = useState<notesGridDTO | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number; rowBottom: number } | null>(null);

  const handlePageChange = useCallback((e: GridPageChangeEvent) => {
    setSkip(e.page.skip);
    setTake(e.page.take);
  }, []);

  const handleSortChange = useCallback((e: GridSortChangeEvent) => {
    setSort(e.sort || []);
    setSkip(0); // Reset to first page when sorting changes
  }, []);

  const handleFilterChange = useCallback((e: GridFilterChangeEvent) => {
    console.log('Filter change event:', JSON.stringify(e.filter, null, 2));
    setFilter(e.filter);
    setSkip(0); // Reset to first page when filtering changes
  }, []);

  const exportToExcel = useCallback(() => {
    const data = paginatedRows.map((note) => ({
      Priority: note.notePriority,
      Type: note.noteType,
      Reference: note.reference,
      Client: note.clientName,
      Site: note.siteAddress,
      "Assigned To": note.assignedTo,
      Details: note.note,
      "Created Date": dayjs(note.createdDate).format("DD/MM/YYYY"),
      Done: note.isCompleted ? "Yes" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notes");
    XLSX.writeFile(workbook, "Notes.xlsx");
  }, [paginatedRows]);

  const handleShowCompleted = (event: any) => {
    setShowCompleted(event.target.value);
  };

  // "All" plus whatever the priority lookup holds, so adding an option to
  // note_priority shows up here without a code change
  const priorityFilterOptions = React.useMemo(
    () => [{ value: ALL_PRIORITIES, text: "All" }, ...priorityOptions],
    [priorityOptions]
  );

  const handlePriorityFilterChange = useCallback((event: any) => {
    setPriorityFilter(event.value?.value ?? ALL_PRIORITIES);
    setSkip(0); // The filtered set is a different length, so go back to page one
  }, [setPriorityFilter]);

  // The picker only appears once somebody else is in the list; with no grants
  // there is nothing to choose between and the screen stays as it was.
  const canViewOtherEmployees = viewableEmployees.length > 1;
  // The first entry is always the signed in user, which is the default view.
  const ownEmployee = viewableEmployees[0] ?? null;
  const selectedEmployee =
    viewableEmployees.find((e) => e.employeeId === viewEmployeeId) ?? ownEmployee;

  // Looking at somebody else's list. Completion is per assignee, so acting from
  // here would either complete the note for every assignee at once or silently
  // toggle the viewer's own copy and appear to do nothing - neither of which is
  // what "view their notes" should mean. The grant is read only.
  const isViewingOtherEmployee =
    viewEmployeeId !== null && ownEmployee !== null && viewEmployeeId !== ownEmployee.employeeId;

  const handleViewEmployeeChange = useCallback((event: any) => {
    const employeeId = event.value?.employeeId ?? null;
    setViewEmployeeId(employeeId);
    setSkip(0); // A different person's notes are a different list, so page one
  }, []);

  // Load notes data based on current pagination state
  const loadNotes = useCallback(async () => {
    try {
      const sourceEntity: addNoteDTO = { enquiryId: null, quoteId: null, jobId: null, invoiceId: null };
      const result = await getNotesWithPagination({
        sourceEntity,
        skip,
        take,
        sort,
        filter,
        showCompleted,
        notePriorityId: priorityFilter === ALL_PRIORITIES ? null : priorityFilter,
        viewEmployeeId,
      });
      setPaginatedRows(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error("Failed to load notes:", error);
    }
  }, [skip, take, sort, filter, showCompleted, priorityFilter, viewEmployeeId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  useEffect(() => {
    getNotePriorities()
      .then((priorities: notePriority[]) =>
        setPriorityOptions(priorities.map((p) => ({ value: p.notePriorityId, text: p.name })))
      )
      .catch((error) => console.error("Failed to load note priorities:", error));
  }, []);

  useEffect(() => {
    getViewableEmployees()
      .then(setViewableEmployees)
      // The screen still works on the user's own notes if this fails; only the
      // picker is lost, so it is not worth a toast.
      .catch((error) => console.error("Failed to load viewable employees:", error));
  }, []);

  // Get current user's employee info
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        console.log('User data from localStorage:', userData);
        
        // Try different possible field combinations
        let employeeId = userData.employeeId || userData.EmployeeId || userData.id;
        let name = userData.name || userData.Name || userData.fullName || userData.FullName || userData.username || userData.userName;
        
        if (employeeId && name) {
          const userEmployee = {
            value: employeeId,
            text: name
          };
          console.log('Setting current user employee:', userEmployee);
          setCurrentUserEmployee(userEmployee);
        } else {
          console.log('Could not find employeeId or name in user data');
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    } else {
      console.log('No user found in localStorage');
    }
  }, []);

  // Refresh when showCompleted changes
  useEffect(() => {
    setSkip(0);
    // Directly fetch with updated showCompleted
    const fetchData = async () => {
      try {
        const sourceEntity: addNoteDTO = { enquiryId: null, quoteId: null, jobId: null, invoiceId: null };
        const result = await getNotesWithPagination({
          sourceEntity,
          skip: 0,
          take,
          sort,
          filter,
          showCompleted,
          viewEmployeeId,
        });
        setPaginatedRows(result.data);
        setTotal(result.total);
      } catch (error) {
        console.error("Failed to load notes:", error);
      }
    };
    fetchData();
  }, [showCompleted, take, sort, filter, viewEmployeeId]);

  const rowRender = useCallback(
    (trElement: any, rowProps: any) => {
      const dataItem = rowProps.dataItem;
      const bgColor = dataItem.isCompleted ? "#d7edd7" : "rgb(245 249 251)";

      const newTrElement = React.cloneElement(
        trElement,
        {
          style: { backgroundColor: bgColor, border: "1px solid rgb(216 229 237)" },
          // Completion is per assignee, and the grid is too tight for a column
          // per person, so the breakdown lives in a hover panel instead.
          onMouseEnter: (e: React.MouseEvent<HTMLTableRowElement>) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setHoveredNote(dataItem);
            setHoverPosition({ x: rect.left + 40, y: rect.top, rowBottom: rect.bottom });
          },
          onMouseLeave: () => {
            setHoveredNote(null);
            setHoverPosition(null);
          },
        },
        trElement.props.children
      );

      return newTrElement;
    },
    []
  );

  const handleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
    setAddFormVisible(false);
    setViewFormVisible(false);
  }, []);

  const handleAddForm = useCallback(() => {
    setAddFormVisible(true);
    setViewFormVisible(false);
    setExpanded(true);
  }, []);

  const handleViewNote = useCallback((note: notesGridDTO) => {
    setSelectedNote(note);
    setViewFormVisible(true);
    setAddFormVisible(false);
    setExpanded(true);
  }, []);

  const handleToggleCompleted = useCallback(async () => {
    if (!selectedNote) return;
    
    try {
      await toggleCompletedNote(selectedNote.noteId);
      showToast(`Note marked as ${selectedNote.isCompleted ? 'pending' : 'completed'}`, "success");
      
      // Update the selected note state
      setSelectedNote({
        ...selectedNote,
        isCompleted: !selectedNote.isCompleted
      });
      
      // Refresh the grid
      await loadNotes();
    } catch (error: any) {
      console.error("Failed to toggle note status:", error);
      showToast("Failed to update note status", "error");
    }
  }, [selectedNote, showToast, loadNotes]);

  const handlePriorityChange = useCallback(async (notePriorityId: number) => {
    if (!selectedNote || notePriorityId === selectedNote.notePriorityId) return;

    try {
      await updateNotePriority(selectedNote.noteId, notePriorityId);

      const priority = priorityOptions.find((p) => p.value === notePriorityId);
      setSelectedNote({
        ...selectedNote,
        notePriorityId,
        notePriority: priority?.text ?? selectedNote.notePriority,
      });

      showToast(`Priority set to ${priority?.text ?? 'Normal'}`, "success");

      // Re-orders the grid, since priority drives the ordering
      await loadNotes();
    } catch (error: any) {
      console.error("Failed to update note priority:", error);
      showToast("Failed to update note priority", "error");
    }
  }, [selectedNote, priorityOptions, showToast, loadNotes]);

  const handleSubmitNote = useCallback(async (dataItem: any) => {
    try {
      console.log("Form data received:", dataItem);
      
      // Validate that we have employees assigned
      if (!dataItem.employeesAssignedToId || dataItem.employeesAssignedToId.length === 0) {
        showToast("Please select at least one employee", "error");
        return;
      }
      
      const employeeIds = dataItem.employeesAssignedToId.map((emp: { value: number; text: string }) => emp.value);
      const dueDateValue = dataItem.dueDate ? new Date(dataItem.dueDate) : undefined;
      // The dropdown hands back the whole option; untouched means Normal
      const priorityId = dataItem.notePriorityId?.value ?? NOTE_PRIORITY_NORMAL;

      console.log("Submitting note with:", { note: dataItem.note, employeeIds, dueDate: dueDateValue, priorityId });

      await addStandaloneNote(dataItem.note, employeeIds, dueDateValue, priorityId);
      showToast("Note successfully created", "success");
      
      // Close the drawer
      setAddFormVisible(false);
      setExpanded(false);
      
      // Refresh the grid
      await loadNotes();
    } catch (error: any) {
      console.error("Failed to create note:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
      showToast("Failed to create note", "error");
    }
  }, [showToast, loadNotes]);

  return (
    <div className="p-2">
      <style>{filterInputStyles}</style>
      <div className="grid__title__container" style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
        <div className="grid__header__background">
          <div className="grid__title">
            <div>Notes</div>
            <div style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
              <div onClick={handleAddForm} className="grid__export__button">
                <FaPlus style={{ fontSize: "14pt", marginRight: "1rem" }} />
                <p style={{ margin: 0 }}>Add Note</p>
              </div>
              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel style={{ fontSize: "14pt", marginRight: "1rem" }} />
                <p style={{ margin: 0 }}>Export to Excel</p>
              </div>
            </div>
          </div>

          <div className="jobs__header__controls" style={{ marginTop: "1rem" }}>
            <div className="jobs__header__control">
              <label>Priority:</label>
              <DropDownList
                data={priorityFilterOptions}
                textField="text"
                dataItemKey="value"
                value={
                  priorityFilterOptions.find((option) => option.value === priorityFilter) ??
                  priorityFilterOptions[0]
                }
                onChange={handlePriorityFilterChange}
                style={{ width: "140px" }}
              />
            </div>
            {canViewOtherEmployees && (
              <div className="jobs__header__control">
                <label>Notes For:</label>
                <DropDownList
                  data={viewableEmployees}
                  textField="employeeName"
                  dataItemKey="employeeId"
                  value={selectedEmployee}
                  onChange={handleViewEmployeeChange}
                  style={{ width: "180px" }}
                />
              </div>
            )}
            <div className="jobs__header__control">
              <label>Show Completed Notes:</label>
              <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowCompleted}
                checked={showCompleted}
              />
            </div>
          </div>
        </div>
      </div>
      <Grid
        data={paginatedRows}
        total={total}
        skip={skip}
        take={take}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        sort={sort}
        filter={filter}
        sortable={true}
        filterable={true}
        pageable={{ pageSizes: [10, 20, 30, 40, 50] }}
        rowRender={rowRender}
        onRowClick={(e) => handleViewNote(e.dataItem)}
        style={{ cursor: 'pointer' }}
      >
        <GridColumn
          field="notePriority"
          title=" "
          width="50px"
          filterable={false}
          // The list is always ordered by priority, so sorting this column would lie
          sortable={false}
          cell={(cellProps: any) => (
            <td style={{ textAlign: 'center' }}>
              <PriorityIcon priority={cellProps.dataItem.notePriority} />
            </td>
          )}
        />
        <GridColumn
          field="noteType"
          title="Type"
          width="120px"
          filterable={true}
          sortable={true}
          filterCell={TypeFilterCell}
        />
        <GridColumn
          field="reference"
          title="Reference"
          width="120px"
          filterable={true}
          sortable={true}
          filterCell={ReferenceFilterCell}
          cell={(cellProps: any) => {
            const item = cellProps.dataItem;
            const handleRefClick = () => {
              let url = '';
              if (item.noteType === 'Enquiry' && item.enquiryId) {
                url = `/enquiries?id=${item.enquiryId}`;
              } else if (item.noteType === 'Quote' && item.quoteId) {
                url = `/quotes?id=${item.quoteId}`;
              } else if (item.noteType === 'Job' && item.jobId) {
                url = `/jobs?id=${item.jobId}`;
              } else if (item.noteType === 'Invoice' && item.invoiceId) {
                url = `/invoicing?id=${item.invoiceId}`;
              } else if (item.noteType === 'Service Reminder' && item.serviceReminderId) {
                url = `/service-reminders?id=${item.serviceReminderId}`;
              }
              
              if (url) {
                window.open(url, '_blank');
              }
            };
            
            const isClickable = (item.noteType === 'Enquiry' && item.enquiryId) ||
                               (item.noteType === 'Quote' && item.quoteId) ||
                               (item.noteType === 'Job' && item.jobId) ||
                               (item.noteType === 'Invoice' && item.invoiceId) ||
                               (item.noteType === 'Service Reminder' && item.serviceReminderId);
            
            return (
              <td>
                {isClickable ? (
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleRefClick();
                    }}
                    style={{ cursor: 'pointer', color: '#0066cc', textDecoration: 'underline' }}
                  >
                    {item.reference}
                  </a>
                ) : (
                  item.reference
                )}
              </td>
            );
          }}
        />
        <GridColumn
          field="clientName"
          title="Client"
          width="100px"
          filterable={false}
          sortable={true}
          cell={(cellProps: any) => {
            const clientName = cellProps.dataItem.clientName || '';
            return (
              <td title={clientName}>
                {clientName || '-'}
              </td>
            );
          }}
        />
        <GridColumn
          field="siteAddress"
          title="Site Address"
          width="280px"
          filterable={false}
          sortable={true}
          cell={(cellProps: any) => {
            const siteAddress = cellProps.dataItem.siteAddress || '';
            return (
              // Grids.css truncates every td (nowrap + max-width 200px + ellipsis),
              // so the full address needs both the wrap and the width cap lifted.
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 'none' }}>
                {siteAddress || '-'}
              </td>
            );
          }}
        />
        <GridColumn
          field="note"
          title="Details"
          width="400px"
          filterable={true}
          sortable={true}
          filterCell={DetailsTextFilterCell}
          cell={(cellProps: any) => {
            const details = cellProps.dataItem.note || '';
            return (
              // Grids.css truncates every td (nowrap + max-width 200px + ellipsis),
              // so showing the full note needs both the wrap and the width cap lifted.
              <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 'none' }}>
                {details || '-'}
              </td>
            );
          }}
        />
        <GridColumn
          field="createdDate"
          title="Date Created"
          width="140px"
          filterable={false}
          sortable={true}
          cell={(cellProps: any) => (
            <td>
              <span>{dayjs(cellProps.dataItem.createdDate).format("DD/MM/YYYY")}</span>
            </td>
          )}
        />
        <GridColumn
          field="dueDate"
          title="Due Date"
          width="140px"
          filterable={false}
          sortable={true}
          cell={(cellProps: any) => (
            <td>
              <span>{cellProps.dataItem.dueDate ? dayjs(cellProps.dataItem.dueDate).format("DD/MM/YYYY") : '-'}</span>
            </td>
          )}
        />
      </Grid>

      {/* View Note Detail Drawer */}
      <Drawer
        expanded={viewFormVisible}
        mode='overlay'
        position='end'
        width={600}
        onOverlayClick={handleExpanded}
      >
        <DrawerContent>
          <DrawerNavigation>
            <div className='form__title__container'>
              <h1 className='form__title'>Note Details</h1>
              <FaTimesCircle
                className='closeform'
                onClick={handleExpanded}
              />
            </div>
            <div className='form__wrapper' style={{ padding: '1rem' }}>
              {selectedNote && (
                <div>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Type:</label>
                    <div>{selectedNote.noteType}</div>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Priority:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <PriorityIcon priority={selectedNote.notePriority} />
                      <DropDownList
                        data={priorityOptions}
                        textField="text"
                        dataItemKey="value"
                        value={
                          priorityOptions.find((p) => p.value === selectedNote.notePriorityId) ??
                          priorityOptions.find((p) => p.value === NOTE_PRIORITY_NORMAL) ?? null
                        }
                        onChange={(e) => handlePriorityChange(e.value.value)}
                        disabled={isViewingOtherEmployee}
                        style={{ width: '160px' }}
                      />
                    </div>
                  </div>

                  {selectedNote.reference && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Reference:</label>
                      <div>{selectedNote.reference}</div>
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Note:</label>
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selectedNote.note}</div>
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Assigned To:</label>
                    <div>
                      {selectedNote.assignments && selectedNote.assignments.length > 0 ? (
                        <CompletionBreakdown note={selectedNote} />
                      ) : selectedNote.assignedEmployees && selectedNote.assignedEmployees.length > 0 ? (
                        selectedNote.assignedEmployees.map((emp) => (
                          <div key={emp.employeeId}>{emp.employeeName}</div>
                        ))
                      ) : (
                        <div>{selectedNote.assignedTo || 'N/A'}</div>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Created Date:</label>
                    <div>{dayjs(selectedNote.createdDate).format('DD/MM/YYYY HH:mm')}</div>
                  </div>
                  
                  {selectedNote.dueDate && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Due Date:</label>
                      <div>{dayjs(selectedNote.dueDate).format('DD/MM/YYYY')}</div>
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Created By:</label>
                    <div>{selectedNote.createdBy}</div>
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Status:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        backgroundColor: selectedNote.isCompleted ? '#d7edd7' : '#ffd699',
                        color: selectedNote.isCompleted ? '#2d5016' : '#664400'
                      }}>
                        {selectedNote.isCompleted ? 'Completed' : 'Pending'}
                      </span>
                      {!isViewingOtherEmployee && (
                        <button
                          onClick={handleToggleCompleted}
                          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                          style={{ padding: '0.5rem 1rem' }}
                        >
                          Mark as {selectedNote.isCompleted ? 'Pending' : 'Completed'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DrawerNavigation>
        </DrawerContent>
      </Drawer>

      {/* Add Note Drawer */}
      <Drawer
        expanded={addFormVisible}
        mode='overlay'
        position='end'
        width={600}
        onOverlayClick={handleExpanded}
      >
        <DrawerContent>
          <DrawerNavigation>
            <div className='form__title__container'>
              <h1 className='form__title'>Add Note</h1>
              <FaTimesCircle
                className='closeform'
                onClick={handleExpanded}
              />
            </div>
            <div className='form__wrapper'>
              <AddNoteForm 
                onNoteSubmit={handleSubmitNote}
                initialValues={{
                  employeesAssignedToId: currentUserEmployee ? [currentUserEmployee] : []
                }}
              />
            </div>
          </DrawerNavigation>
        </DrawerContent>
      </Drawer>

      {/* Completion hover panel */}
      {hoveredNote && hoverPosition && (() => {
        const PANEL_HEIGHT_ESTIMATE = 40 + (hoveredNote.assignments?.length || 1) * 24;
        const openDownwards = hoverPosition.y < PANEL_HEIGHT_ESTIMATE + 16;

        return (
          <div
            style={{
              position: 'fixed',
              left: `${hoverPosition.x}px`,
              top: openDownwards
                ? `${hoverPosition.rowBottom + 8}px`
                : `${hoverPosition.y - 8}px`,
              transform: openDownwards ? 'none' : 'translateY(-100%)',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              padding: '0.6rem 0.85rem',
              minWidth: '280px',
              maxWidth: '420px',
              zIndex: 10000,
              fontSize: '0.8rem',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                fontWeight: 600,
                marginBottom: '0.4rem',
                paddingBottom: '0.3rem',
                borderBottom: '1px solid #eee',
              }}
            >
              Completion
            </div>
            <CompletionBreakdown note={hoveredNote} />
          </div>
        );
      })()}
    </div>
  );
};

export default NotesScreen;
